/* Storefront Master — theme.js
   Small, dependency-free. Behaviour mirrors the prototype's Component logic. */
(() => {
  const SF = (window.SF = window.SF || {});
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));

  /* ---------- Money ---------- */
  SF.money = (cents, format = SF.moneyFormat || '${{amount}}') => {
    if (cents == null || isNaN(cents)) return '';
    cents = Math.round(Number(cents));
    const fmt = (n, d, ts = ',', ds = '.') => {
      const [i, f] = (n / 100).toFixed(d).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, ts) + (f ? ds + f : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => ({
      amount: fmt(cents, 2),
      amount_no_decimals: fmt(cents, 0),
      amount_with_comma_separator: fmt(cents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: fmt(cents, 0, '.', ','),
      amount_with_apostrophe_separator: fmt(cents, 2, "'", '.'),
      amount_with_space_separator: fmt(cents, 2, ' ', ','),
      amount_no_decimals_with_space_separator: fmt(cents, 0, ' ', ',')
    }[k] ?? fmt(cents, 2)));
  };
  const pctOff = (p, c) => (p != null && c && c > p ? Math.round((1 - p / c) * 100) : 0);
  const tierQty = (v) => { const m = String(v || '').match(/\d+/); return m ? Math.max(1, parseInt(m[0], 10)) : 1; };

  /* ---------- Cart ---------- */
  SF.cart = {
    sectionId: 'cart-dropdown',
    async add(items) {
      const res = await fetch(SF.routes.cartAdd + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: this.sectionId, sections_url: location.pathname })
      });
      const json = await res.json();
      if (!res.ok || json.status) throw new Error(json.description || json.message || SF.strings.error);
      this.render(json.sections);
      SF.header && SF.header.openCart();
      document.dispatchEvent(new CustomEvent('sf:cart:updated'));
      return json;
    },
    async change(line, quantity, extraSections = []) {
      const res = await fetch(SF.routes.cartChange + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: [this.sectionId, ...extraSections].join(','), sections_url: location.pathname })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.description || SF.strings.error);
      this.render(json.sections);
      return json;
    },
    render(sections) {
      if (!sections || !sections[this.sectionId]) return;
      const doc = new DOMParser().parseFromString(sections[this.sectionId], 'text/html');
      const inner = $('[data-sf-cart-panel-inner]', doc);
      const panel = $('[data-sf-cart-panel]');
      if (inner && panel) panel.innerHTML = inner.innerHTML;
      const count = $('[data-sf-cart-count-value]', doc);
      if (count) $$('[data-sf-cart-count]').forEach((b) => { b.textContent = count.textContent.trim(); b.hidden = count.textContent.trim() === '0'; });
    }
  };

  /* ---------- Header: mobile menu + cart dropdown ---------- */
  class SfHeader extends HTMLElement {
    connectedCallback() {
      SF.header = this;
      this.menu = $('[data-sf-menu]', this);
      this.panel = $('[data-sf-cart-panel]', this);
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-sf-menu-toggle]')) { e.preventDefault(); this.toggleMenu(); }
        else if (e.target.closest('[data-sf-cart-toggle]')) { e.preventDefault(); this.toggleCart(); }
        else if (e.target.closest('[data-sf-cart-close-link]')) { this.closeAll(); }
      });
      document.addEventListener('click', (e) => { if (!this.contains(e.target) && !e.target.closest('[data-sf-add]')) this.closeAll(); });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.closeAll(); });
    }
    set(el, open, toggleSel) {
      if (!el) return;
      el.hidden = !open;
      $$(toggleSel, this).forEach((b) => b.setAttribute('aria-expanded', open));
    }
    toggleMenu() { const o = this.menu.hidden; this.set(this.panel, false, '[data-sf-cart-toggle]'); this.set(this.menu, o, '[data-sf-menu-toggle]'); }
    toggleCart() { const o = this.panel.hidden; this.set(this.menu, false, '[data-sf-menu-toggle]'); this.set(this.panel, o, '[data-sf-cart-toggle]'); }
    openCart() { this.set(this.menu, false, '[data-sf-menu-toggle]'); this.set(this.panel, true, '[data-sf-cart-toggle]'); }
    closeAll() { this.set(this.menu, false, '[data-sf-menu-toggle]'); this.set(this.panel, false, '[data-sf-cart-toggle]'); }
  }
  customElements.define('sf-header', SfHeader);

  /* ---------- Announcement rotation ---------- */
  class SfAnnouncement extends HTMLElement {
    connectedCallback() {
      this.items = $$('[data-sf-ann-item]', this);
      this.i = 0;
      if (this.dataset.rotate !== 'true' || this.items.length < 2) return;
      this.addEventListener('click', (e) => { const b = e.target.closest('[data-dir]'); if (b) { this.go(this.i + Number(b.dataset.dir)); this.start(); } });
      this.start();
    }
    disconnectedCallback() { clearInterval(this.t); }
    start() { clearInterval(this.t); this.t = setInterval(() => this.go(this.i + 1), Number(this.dataset.interval) || 6000); }
    go(i) { const n = this.items.length; this.i = (i + n) % n; this.items.forEach((el, k) => (el.hidden = k !== this.i)); }
  }
  customElements.define('sf-announcement', SfAnnouncement);

  /* ---------- Product controller (shared by buy box, final offer, sticky bar) ---------- */
  class ProductController {
    constructor(data) {
      this.data = data;
      this.id = data.id;
      this.bidx = data.bundleIndex;
      this.variant = data.variants.find((v) => v.id === data.selected) || data.variants[0];
      this.qty = 1;
      this.subs = new Set();
    }
    find(opts) { return this.data.variants.find((v) => v.options.every((o, i) => o === opts[i])); }
    setOption(i, val) {
      const opts = [...this.variant.options]; opts[i] = val;
      const v = this.find(opts) || this.data.variants.find((x) => x.options[i] === val && x.available) || this.data.variants.find((x) => x.options[i] === val);
      if (v) this.setVariant(v, true);
    }
    /* BUNDLE MODEL: tier = value of the bundle option. Swap here for another pricing model. */
    tierVariant(val) { if (this.bidx < 0) return null; const o = [...this.variant.options]; o[this.bidx] = val; return this.find(o); }
    setTier(val) { if (this.bidx >= 0) this.setOption(this.bidx, val); }
    get tierLabel() { return this.bidx >= 0 ? this.variant.options[this.bidx] : ''; }
    setVariant(v, user) { this.variant = v; this.emit(user ? 'variant' : 'init'); }
    setQty(n) { this.qty = Math.max(1, Math.min(10, n)); this.emit('qty'); }
    subscribe(fn) { this.subs.add(fn); fn(this, 'init'); }
    emit(why) { this.subs.forEach((fn) => fn(this, why)); }
    add() { return SF.cart.add([{ id: this.variant.id, quantity: this.qty }]); }
  }
  SF.products = {};
  SF.product = (id) => {
    id = String(id);
    if (SF.products[id]) return SF.products[id];
    const el = $(`[data-sf-product-json="${id}"]`);
    if (!el) return null;
    const c = (SF.products[id] = new ProductController(JSON.parse(el.textContent)));
    c.subscribe(renderShared);
    return c;
  };

  /* Updates everything outside the buy box that mirrors the product (tiers, final offer, sticky). */
  function renderShared(c) {
    const v = c.variant;
    $$(`[data-sf-tier][data-sf-product="${c.id}"]`).forEach((el) => {
      const val = el.dataset.sfTierValue;
      el.setAttribute('aria-checked', String(val === c.tierLabel));
      const tv = c.tierVariant(val);
      const set = (k, txt, show = true) => { const n = $(`[data-sf-t="${k}"]`, el); if (n) { if (txt != null) n.textContent = txt; n.hidden = !show; } };
      if (!tv) return;
      const p = pctOff(tv.price, tv.compare), q = Number(el.dataset.sfTierQty) || tierQty(val);
      set('price', SF.money(tv.price));
      set('was', p ? SF.money(tv.compare) : '', p > 0);
      set('save', null, p > 0);
      set('pct', p + '%');
      set('each', `${SF.money(tv.price / q)} ${SF.strings.each}`, q > 1);
    });
    $$(`[data-sf-atc-final][data-sf-product="${c.id}"]`).forEach((b) => {
      if (b.dataset.added) return;
      b.textContent = v.available ? `${b.dataset.cta} · ${SF.money(v.price)}` : SF.strings.soldOut;
      b.disabled = !v.available;
    });
    $$(`[data-sf-sticky-sub][data-sf-product="${c.id}"]`).forEach((s) => { s.textContent = [c.tierLabel, SF.money(v.price)].filter(Boolean).join(' · '); });
    $$(`[data-sf-sticky-add][data-sf-product="${c.id}"]`).forEach((b) => { b.disabled = !v.available; });
  }

  async function addWithFeedback(c, btn, label) {
    if (!c || btn.disabled) return;
    btn.disabled = true;
    try {
      await c.add();
      btn.dataset.added = '1';
      const prev = btn.textContent;
      btn.textContent = '✓ ' + SF.strings.added;
      setTimeout(() => { delete btn.dataset.added; btn.disabled = false; btn.textContent = label ? label() : prev; c.emit('refresh'); }, 1600);
    } catch (err) {
      btn.disabled = false;
      const errEl = btn.closest('form') && $('[data-sf-error]', btn.closest('form'));
      if (errEl) { errEl.textContent = err.message; errEl.hidden = false; } else alert(err.message);
    }
  }

  /* Global delegation: tiers + add buttons outside the buy box (final offer, sticky bar). */
  document.addEventListener('click', (e) => {
    const tier = e.target.closest('[data-sf-tier]');
    if (tier) {
      if (tier.hasAttribute('data-sf-demo')) { // placeholder tiers: visual selection only
        const group = tier.parentElement;
        $$('[data-sf-tier]', group).forEach((t) => t.setAttribute('aria-checked', String(t === tier)));
        return;
      }
      const c = SF.product(tier.dataset.sfProduct);
      if (c) c.setTier(tier.dataset.sfTierValue);
      return;
    }
    const add = e.target.closest('[data-sf-add][data-sf-product]');
    if (add) {
      e.preventDefault();
      const c = SF.product(add.dataset.sfProduct);
      addWithFeedback(c, add);
    }
  });
  document.addEventListener('keydown', (e) => {
    const tier = e.target.closest && e.target.closest('[role="radio"][data-sf-tier]');
    if (tier && (e.key === ' ' || e.key === 'Enter')) { e.preventDefault(); tier.click(); }
  });

  /* ---------- Buy box ---------- */
  class SfProduct extends HTMLElement {
    connectedCallback() {
      this.gallery();
      this.c = SF.product(this.dataset.productId);
      if (!this.c) return; // placeholder mode (no product)
      this.form = $('[data-sf-form]', this) || $('form[action*="/cart/add"]', this);
      this.addEventListener('click', (e) => {
        const opt = e.target.closest('[data-sf-option]');
        if (opt) return this.c.setOption(Number(opt.dataset.sfOption), opt.dataset.value);
        const step = e.target.closest('[data-sf-qty-step]');
        if (step) return this.c.setQty(this.c.qty + Number(step.dataset.sfQtyStep));
      });
      if (this.form) this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        const btn = $('[data-sf-atc-main]', this);
        addWithFeedback(this.c, btn, () => this.atcLabel());
      });
      this.c.subscribe((c, why) => this.render(c, why));
    }
    atcLabel() {
      const v = this.c.variant;
      return v.available ? `${this.dataset.cta} · ${SF.money(v.price * this.c.qty)}` : SF.strings.soldOut;
    }
    render(c, why) {
      const v = c.variant, p = pctOff(v.price, v.compare);
      const q = (k) => $(`[data-sf-p="${k}"]`, this);
      if (q('price')) q('price').textContent = SF.money(v.price);
      if (q('was')) { q('was').textContent = p ? SF.money(v.compare) : ''; q('was').hidden = !p; }
      if (q('save')) q('save').hidden = !p;
      if (q('pct')) q('pct').textContent = p + '%';
      $$('[data-sf-option]', this).forEach((b) => b.setAttribute('aria-checked', String(v.options[Number(b.dataset.sfOption)] === b.dataset.value)));
      $$('[data-sf-option-name]', this).forEach((n) => (n.textContent = v.options[Number(n.dataset.sfOptionName)]));
      const input = $('[data-sf-variant-input]', this); if (input) input.value = v.id;
      const qi = $('[data-sf-qty-input]', this); if (qi) qi.value = c.qty;
      const qv = $('[data-sf-qty-value]', this); if (qv) qv.textContent = c.qty;
      const dec = $('[data-sf-qty-step="-1"]', this); if (dec) dec.dataset.dim = String(c.qty <= 1);
      const atc = $('[data-sf-atc-main]', this);
      if (atc && !atc.dataset.added) { atc.textContent = this.atcLabel(); atc.disabled = !v.available; }
      if (why === 'variant') {
        if (this.dataset.updateUrl === 'true') { const u = new URL(location.href); u.searchParams.set('variant', v.id); history.replaceState({}, '', u); }
        if (v.media) { const i = $$('[data-media-id]', this).findIndex((s) => s.dataset.mediaId === String(v.media)); if (i >= 0) this.go(i); }
      }
    }
    gallery() {
      this.track = $('[data-sf-gal-track]', this);
      if (!this.track) return;
      this.addEventListener('click', (e) => { const g = e.target.closest('[data-sf-gal-go]'); if (g) this.go(Number(g.dataset.sfGalGo)); });
      let raf;
      this.track.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => this.mark(Math.round(this.track.scrollLeft / this.track.clientWidth))); }, { passive: true });
    }
    go(i) {
      const n = this.track.children.length; if (!n) return;
      i = (i + n) % n;
      this.track.scrollTo({ left: i * this.track.clientWidth, behavior: 'smooth' });
      this.mark(i);
    }
    mark(i) { $$('[data-sf-gal-go]', this).forEach((b) => b.setAttribute('aria-current', String(Number(b.dataset.sfGalGo) === i))); }
  }
  customElements.define('sf-product', SfProduct);

  /* ---------- Sticky add-to-cart (prototype: visible once main ATC scrolled past, hidden while #offer is on screen) ---------- */
  class SfSticky extends HTMLElement {
    connectedCallback() {
      const bar = $('[data-sf-sticky-bar]', this);
      const atc = $('[data-sf-atc-main]');
      if (!bar || !atc) return;
      SF.product(this.dataset.productId);
      let below = false, offerVis = false;
      const upd = () => (bar.hidden = !(below && !offerVis));
      new IntersectionObserver(([e]) => { below = !e.isIntersecting && e.boundingClientRect.top < 0; upd(); }).observe(atc);
      const offer = document.getElementById('offer');
      if (offer) new IntersectionObserver(([e]) => { offerVis = e.isIntersecting; upd(); }).observe(offer);
    }
  }
  customElements.define('sf-sticky', SfSticky);

  /* Final offer: make sure the controller exists so its pills/labels render. */
  $$('[data-sf-final]').forEach((el) => SF.product(el.dataset.sfProduct));
  document.addEventListener('DOMContentLoaded', () => $$('[data-sf-final]').forEach((el) => SF.product(el.dataset.sfProduct)));

  /* ---------- Exclusive accordions fallback (<details name> unsupported) ---------- */
  document.addEventListener('toggle', (e) => {
    const d = e.target;
    if (!(d instanceof HTMLDetailsElement) || !d.open || !d.getAttribute('name')) return;
    $$(`details[name="${d.getAttribute('name')}"]`).forEach((o) => { if (o !== d) o.open = false; });
  }, true);

  /* ---------- Cart page ---------- */
  class SfCartPage extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', async (e) => {
        const b = e.target.closest('[data-sf-line]');
        if (!b) return;
        e.preventDefault();
        const box = $('.sf-cartpg', this) || this;
        box.classList.add('is-busy');
        try {
          const json = await SF.cart.change(Number(b.dataset.sfLine), Number(b.dataset.qty), [this.dataset.sectionId]);
          const html = json.sections && json.sections[this.dataset.sectionId];
          if (html) {
            const fresh = $('sf-cart-page', new DOMParser().parseFromString(html, 'text/html'));
            if (fresh) this.innerHTML = fresh.innerHTML;
          }
        } catch (err) { alert(err.message); }
        box.classList.remove('is-busy');
      });
    }
  }
  customElements.define('sf-cart-page', SfCartPage);
})();
